import { useEffect, useRef, useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { relative } from "@/lib/relative-time"
import {
  COMMUNITY_STATUS_LABELS,
  COMMUNITY_STATUS_TONES,
  STATUS_LABELS,
  STATUS_TONES,
  type CommunityStatus,
  type ProjectStatus,
} from "@/lib/project-status"

// The Projects landing (spec §5A): recency first, search second, directory
// last. No customer rail — the hierarchy is entered from a specific thing you
// named, not by scanning a list.

type WorkCommunity = {
  id: number
  name: string
  place: string
  status: CommunityStatus
  pinned: boolean
  plans: number
  lots: number
  visited_at: string | null
}

type RecentLot = {
  id: number
  display_name: string
  plan: string
  customer: string
  status: ProjectStatus
  visited_at: string
}

type DirectoryCustomer = {
  id: number
  name: string
  customer_type: string
  communities: number
  open_lots: number
  activity: string
}

type Directory = {
  customers: DirectoryCustomer[]
  type_counts: Record<string, number>
  total: number
  page: number
  per_page: number
  type: string | null
}

type SearchItem = {
  glyph: string
  name: string
  path: string
  meta: string | null
  url: string
}

type SearchGroup = { label: string; items: SearchItem[] }

type Props = {
  view: "work" | "directory" | "search"
  filters: { q: string }
  communities?: WorkCommunity[]
  recent_lots?: RecentLot[]
  directory?: Directory
  results?: SearchGroup[]
}

const TYPE_LABELS: Record<string, string> = {
  production_builder: "Builders",
  custom_builder: "Custom builders",
  retail: "Retail",
  commercial: "Commercial",
  multifamily: "Multifamily",
}

export default function ProjectsIndex({
  view,
  filters,
  communities = [],
  recent_lots = [],
  directory,
  results = [],
}: Props) {
  const [q, setQ] = useState(filters.q)
  const searchRef = useRef<HTMLInputElement>(null)
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // ⌘K focuses the search from anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  const search = (value: string) => {
    setQ(value)
    clearTimeout(debounce.current)
    debounce.current = setTimeout(() => {
      router.get(
        "/projects",
        value.trim() === "" ? {} : { view: "search", q: value },
        { preserveState: true, replace: true },
      )
    }, 250)
  }

  const switchView = (next: "work" | "directory") => {
    setQ("")
    router.get("/projects", next === "work" ? {} : { view: next }, { preserveState: true })
  }

  return (
    <>
      <Head title="Projects">
        <meta name="description" content="Your communities and recent lots — search everything by lot number, plan, community, or customer." />
        <meta property="og:title" content="Projects" />
        <meta property="og:description" content="Your communities and recent lots — search everything by lot number, plan, community, or customer." />
      </Head>
      <AppShell wide>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1>Projects</h1>
            <p className="mt-1 text-sm text-ink-muted">
              Nothing is listed until you ask for it — recency first, search second, directory last.
            </p>
          </div>
          <span className="flex items-center gap-2">
            <button
              onClick={() => switchView("work")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                view === "work" ? "bg-ink-display text-page" : "bg-surface text-ink-muted"
              }`}
            >
              Your work
            </button>
            <button
              onClick={() => switchView("directory")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                view === "directory" ? "bg-ink-display text-page" : "bg-surface text-ink-muted"
              }`}
              data-testid="directory-view"
            >
              Customer directory
            </button>
            <Button asChild>
              <Link href="/projects/new">New project</Link>
            </Button>
          </span>
        </div>

        <div className="relative mt-5 max-w-2xl">
          <Input
            ref={searchRef}
            value={q}
            onChange={(e) => search(e.target.value)}
            placeholder="Search a lot #, plan, community, or customer…"
            aria-label="Search projects"
            className="w-full pr-14"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 rounded border border-hairline px-1.5 py-0.5 font-mono text-[11px] text-ink-muted">
            ⌘K
          </kbd>
        </div>

        {view === "work" && <WorkView communities={communities} recentLots={recent_lots} />}
        {view === "directory" && directory && <DirectoryView directory={directory} />}
        {view === "search" && <SearchView results={results} q={filters.q} />}
      </AppShell>
    </>
  )
}

function WorkView({
  communities,
  recentLots,
}: {
  communities: WorkCommunity[]
  recentLots: RecentLot[]
}) {
  return (
    <div className="mt-6">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Communities you&apos;re working
        </h2>
        <span className="text-xs text-ink-muted">
          Pinned, plus anything you&apos;ve opened in the last 30 days.
        </span>
      </div>

      {communities.length === 0 ? (
        <div className="callout mt-3 max-w-lg">
          <p>Nothing recent. Open a community from the directory, or star one to keep it here.</p>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {communities.map((community) => (
            <Link
              key={community.id}
              href={`/communities/${community.id}`}
              className="rounded-lg border border-hairline bg-page px-4 py-3.5 text-ink-body no-underline hover:border-accent hover:bg-surface"
            >
              <span className="flex items-start justify-between gap-2">
                <span className="min-w-0">
                  <span className="block truncate text-[15px] font-semibold text-ink-display">
                    {community.pinned && <span aria-label="Pinned">★ </span>}
                    {community.name}
                  </span>
                  <span className="block truncate text-xs text-ink-muted">{community.place}</span>
                </span>
                <Badge tone={COMMUNITY_STATUS_TONES[community.status]}>
                  {COMMUNITY_STATUS_LABELS[community.status]}
                </Badge>
              </span>
              <span className="mt-3 flex gap-4 text-xs text-ink-muted">
                <span>
                  <span className="font-mono font-semibold text-ink-display">{community.plans}</span>{" "}
                  {community.plans === 1 ? "plan" : "plans"}
                </span>
                <span>
                  <span className="font-mono font-semibold text-ink-display">{community.lots}</span>{" "}
                  {community.lots === 1 ? "lot" : "lots"}
                </span>
                {community.visited_at && (
                  <span className="ml-auto">{relative(community.visited_at, { suffix: true })}</span>
                )}
              </span>
            </Link>
          ))}
        </div>
      )}

      {recentLots.length > 0 && (
        <>
          <h2 className="mt-8 text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Lots you touched recently
          </h2>
          <ul className="mt-2 divide-y divide-hairline rounded-lg border border-hairline bg-page">
            {recentLots.map((lot) => (
              <li key={lot.id}>
                <Link
                  href={`/projects/${lot.id}`}
                  className="grid grid-cols-[minmax(120px,1fr)_minmax(150px,1.4fr)_auto_70px] items-center gap-x-3 px-4 py-2.5 text-sm text-ink-body no-underline hover:bg-surface"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink-display">
                      {lot.display_name}
                    </span>
                    <span className="block truncate text-xs text-ink-muted">{lot.customer}</span>
                  </span>
                  <span className="truncate text-xs text-ink-muted">{lot.plan}</span>
                  <Badge tone={STATUS_TONES[lot.status]}>{STATUS_LABELS[lot.status]}</Badge>
                  <span className="text-right text-xs text-ink-muted">{relative(lot.visited_at)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function DirectoryView({ directory }: { directory: Directory }) {
  const { customers, type_counts, total, page, per_page, type } = directory
  const allCount = Object.values(type_counts).reduce((a, b) => a + b, 0)
  const pages = Math.max(1, Math.ceil(total / per_page))

  const setType = (next: string | null) => {
    router.get(
      "/projects",
      { view: "directory", ...(next ? { type: next } : {}) },
      { preserveState: true },
    )
  }
  const setPage = (next: number) => {
    router.get(
      "/projects",
      { view: "directory", ...(type ? { type } : {}), page: next },
      { preserveState: true, preserveScroll: true },
    )
  }

  return (
    <div className="mt-6">
      <p className="text-sm text-ink-muted">
        The full list is a searchable, sorted table — default sort is last activity, so dormant
        customers sink.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <TypeChip label="All" count={allCount} active={type == null} onClick={() => setType(null)} />
        {Object.entries(TYPE_LABELS).map(([key, label]) => (
          <TypeChip
            key={key}
            label={label}
            count={type_counts[key] ?? 0}
            active={type === key}
            onClick={() => setType(key)}
          />
        ))}
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-hairline">
        <div className="grid grid-cols-[minmax(140px,1.6fr)_minmax(100px,1fr)_86px_86px] gap-x-3 border-b border-hairline bg-surface px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
          <span>Customer</span>
          <span>Type</span>
          <span className="text-right">Open lots</span>
          <span className="text-right">Activity</span>
        </div>
        <ul className="divide-y divide-hairline bg-page">
          {customers.map((customer) => (
            <li key={customer.id}>
              <Link
                href={`/projects?view=search&q=${encodeURIComponent(customer.name)}`}
                className="grid grid-cols-[minmax(140px,1.6fr)_minmax(100px,1fr)_86px_86px] items-center gap-x-3 px-4 py-2.5 text-sm text-ink-body no-underline hover:bg-surface"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-ink-display">{customer.name}</span>
                  <span className="block truncate text-xs text-ink-muted">
                    {customer.communities > 0
                      ? `${customer.communities} ${customer.communities === 1 ? "community" : "communities"}`
                      : "no communities"}
                  </span>
                </span>
                <span className="truncate text-xs text-ink-muted">
                  {TYPE_LABELS[customer.customer_type]?.replace(/s$/, "") ?? customer.customer_type}
                </span>
                <span
                  className={`text-right font-mono text-sm ${
                    customer.open_lots > 0 ? "text-ink-display" : "text-ink-muted"
                  }`}
                >
                  {customer.open_lots}
                </span>
                <span className="text-right text-xs text-ink-muted">{relative(customer.activity)}</span>
              </Link>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between border-t border-hairline bg-page px-4 py-2.5 text-sm text-ink-muted">
          <span>
            {total === 0
              ? "No customers"
              : `${(page - 1) * per_page + 1}–${Math.min(page * per_page, total)} of ${total} customers`}
          </span>
          <span className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Prev
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= pages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </span>
        </div>
      </div>
    </div>
  )
}

function TypeChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm ${
        active
          ? "border-accent bg-accent-faded font-semibold text-accent-display"
          : "border-hairline text-ink-body"
      }`}
    >
      {label}
      <span className="font-mono text-xs text-ink-muted">{count}</span>
    </button>
  )
}

function SearchView({ results, q }: { results: SearchGroup[]; q: string }) {
  return (
    <div className="mt-6">
      <p className="text-sm text-ink-muted">
        One box searches lots, plans, communities, and customers at once — top matches per group.
      </p>
      {results.length === 0 ? (
        <div className="callout mt-4 max-w-lg">
          <p>No matches for “{q}”.</p>
        </div>
      ) : (
        results.map((group) => (
          <div key={group.label} className="mt-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
              {group.label}
            </h2>
            <ul className="mt-2 space-y-2">
              {group.items.map((item) => (
                <li key={item.url + item.name}>
                  <Link
                    href={item.url}
                    className="flex items-center gap-3 rounded-lg border border-hairline bg-page px-3.5 py-2.5 text-sm text-ink-body no-underline hover:border-accent hover:bg-surface"
                  >
                    <span aria-hidden className="w-5 shrink-0 text-center text-ink-muted">
                      {item.glyph}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink-display">{item.name}</span>
                      <span className="block truncate text-xs text-ink-muted">{item.path}</span>
                    </span>
                    {item.meta && <span className="shrink-0 text-xs text-ink-muted">{item.meta}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  )
}
